import { Stack, Typography, Box, Tooltip, IconButton, List, ListItem, Slider } from "@mui/material";
import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { HexColorPicker, HexColorInput } from "react-colorful";

import BaseDrawer from "../BaseDrawer";

import { styles } from "./styles";

import AlertDialog from "@/components/AlertDialog";
import { rgbStringToHex, hexToRgbString } from "@/utils/globals";
import { useAppSelector, useAppDispatch } from "@/hooks/useRedux";
import userServices from "@/services/user";
import { setUserOptions } from "@/store/slices/user";
import useDisclosure from "@/hooks/useDisclosure";

interface IDepthShadingDrawer {
  open: boolean;
}

function DepthNumberDrawer({ open }: IDepthShadingDrawer) {
  const dispatch = useAppDispatch();
  const { userOptions } = useAppSelector((state) => state.user);
  const [color, setColor] = useState("#aabbcc");
  const [fontColor, setFontColor] = useState(userOptions?.font_color || "#000");
  const [fontColorDialog, setFontColorDialog] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [fontSize, setFontSize] = useState(userOptions?.font_size ?? 1);

  const handleOpenEditFontColor = (color: string) => {
    const colorHex = rgbStringToHex(color);

    setFontColorDialog(true);
    setFontColor(colorHex);
    onOpen();
  };

  const handleSaveFontColor = async (color: string) => {
    const colorRgb = hexToRgbString(color);

    dispatch(
      setUserOptions({
        ...userOptions,
        font_color: colorRgb,
      }),
    );
    await userServices.editUserOptions({
      ...userOptions,
      font_color: colorRgb,
    });

    setFontColorDialog(false);
    onClose();
  };

  const handleChangeOpacity = (e: Event, newValue: number | number[]) => {
    setFontSize(newValue as number);
  };

  const handleSliderSize = async () => {
    dispatch(
      setUserOptions({
        ...userOptions,
        font_size: fontSize,
      }),
    );
    await userServices.editUserOptions({
      ...userOptions,
      font_size: fontSize,
    });
  };

  return (
    <Box>
      <BaseDrawer open={open} styles={styles.drawer}>
        <Stack component="main" sx={styles.container}>
          <Stack
            alignItems="center"
            component="section"
            direction="row"
            justifyContent="space-between"
            sx={styles.header}
          >
            <Typography variant="h5">Depth Shading</Typography>
          </Stack>
          <List sx={styles.listMainContainer}>
            <ListItem sx={{ ...styles.listItemContainer, pr: 1 }}>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Typography sx={{ width: "100%", maxWidth: "100px", fontSize: "14px" }}>
                  Text Color
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    ...styles.overlay,
                    "&:before": { display: "none" },
                  }}
                >
                  <Stack alignItems="center" direction="row" spacing={1} sx={{ width: "100%" }}>
                    <Box
                      sx={
                        userOptions?.font_color
                          ? {
                              ...styles.colorBoxFont,
                              background: `rgb(${userOptions?.font_color})`,
                            }
                          : { ...styles.colorBoxFont }
                      }
                    />
                  </Stack>
                  <Tooltip arrow placement="top" title="Change Depth Shading">
                    <IconButton
                      className="editButton"
                      color="secondary"
                      size="small"
                      sx={styles.editButtonContainer}
                      onClick={() => handleOpenEditFontColor(userOptions?.font_color || "0,0,0")}
                    >
                      <Box sx={styles.editButton}>
                        <EditIcon sx={{ fontSize: 20 }} />
                      </Box>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </ListItem>
            <ListItem sx={{ ...styles.listItemContainer, pr: 1, mt: 3 }}>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="center"
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Typography sx={{ width: "100%", maxWidth: "100px", fontSize: "14px" }}>
                  Font Size
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    ...styles.overlay,
                    "&:before": { display: "none" },
                  }}
                >
                  <Slider
                    aria-label="transparency"
                    color="secondary"
                    max={2}
                    min={0.75}
                    size="small"
                    step={0.01}
                    value={fontSize}
                    onChange={handleChangeOpacity}
                    onChangeCommitted={handleSliderSize}
                  />
                </Stack>
              </Stack>
            </ListItem>
          </List>
        </Stack>
      </BaseDrawer>
      <AlertDialog
        cancelButtonTitle="Cancel"
        close={onClose}
        confirm={() => handleSaveFontColor(fontColor)}
        dialogContentStyles={{ p: 0 }}
        open={isOpen}
        positiveButtonTitle="Confirm"
        title="Select Color"
      >
        <Stack
          alignItems="center"
          component="section"
          justifyContent="center"
          sx={styles.colorPickerContainer}
        >
          <HexColorPicker
            color={fontColorDialog ? fontColor : color}
            onChange={fontColorDialog ? setFontColor : setColor}
          />
          <Box mt={1.5}>
            <HexColorInput
              color={fontColorDialog ? fontColor : color}
              onChange={fontColorDialog ? setFontColor : setColor}
            />
          </Box>
        </Stack>
      </AlertDialog>
    </Box>
  );
}

export default DepthNumberDrawer;
